import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Flex, Text } from '@raystack/apsara';

import {
  CopilotProvider,
  useCopilotDispatch,
  useCopilotSelector
} from '@src/contexts';
import { Show } from '@src/hoc';
import { MULTI_CHAT_STEPS } from '@src/utils/constants/COPILOT_CONSTANTS';

import { ChatScreen } from './ChatScreen';
import { CopilotFooter } from './Footer';
import { CopilotHeader } from './Header';
import { WelcomeScreen } from './WelcomeScreen';

import styles from './MultiChat.module.css';

const Copilot = () => {
  const footerRef = useRef<{ setMessage: (newMessage: string) => void }>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<{ animationId: number }>({ animationId: 0 });
  const isSending = useCopilotSelector(state => state.isSending);
  const chatData = useCopilotSelector(state => state.chatData);
  const multiChatStep = useCopilotSelector(state => state.multiChatStep);
  // @ts-expect-error @ts-ignore
  const chatId = useSelector(state => state.app.copilotChatId);

  const dispatch = useCopilotDispatch();

  const handleSelectPrompt = (prompt: string) => {
    if (footerRef.current) {
      // Set selected prompt message
      footerRef.current.setMessage(prompt);
    }
  };

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    // scroll to the footer on the next repaint
    animationRef.current.animationId = requestAnimationFrame(() => {
      if (!!el) {
        el.scrollTop = el.scrollHeight;
      }
    });

    const animationId = animationRef.current.animationId;

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [chatData, isSending]);

  useEffect(() => {
    if (chatId) {
      dispatch({ chatId, multiChatStep: MULTI_CHAT_STEPS.CHAT });
    }
  }, [chatId, dispatch]);

  return (
    <Flex className={styles.copilotPanelWrapper} direction='column'>
      <CopilotHeader />
      <div className={styles.content} ref={contentRef}>
        <Show
          when={multiChatStep === MULTI_CHAT_STEPS.WELCOME}
          fallback={<ChatScreen handleSelectPrompt={handleSelectPrompt} />}
        >
          <WelcomeScreen handleSelectPrompt={handleSelectPrompt} />
        </Show>
      </div>

      <CopilotFooter ref={footerRef} />
      <Flex justify='center' align='center' className={styles.disclaimer}>
        <Text variant='secondary' size='mini'>
          Aurora Intelligence can make mistakes. Check important info.
        </Text>
      </Flex>
    </Flex>
  );
};

export const MultiChatPanel = () => {
  // @ts-expect-error @ts-ignore
  const showCoPilot = useSelector(state => state.app.showCoPilot);

  return (
    <Show when={showCoPilot}>
      <CopilotProvider>
        <Copilot />
      </CopilotProvider>
    </Show>
  );
};
